// Глобальный монитор жизненного цикла страницы (Page Lifecycle API) для стабилизации
// критичных операций на мобильных (hidden/frozen) и WakeLock-синглтон.

let wakeLockSentinel = null;
let wakeLockTotalRefCount = 0;
const wakeLockReasonCounts = new Map();

async function tryAcquireWakeLock() {
  if (typeof navigator === 'undefined') return;
  if (!('wakeLock' in navigator)) return;
  if (wakeLockSentinel) return;

  try {
    wakeLockSentinel = await navigator.wakeLock.request('screen');
    wakeLockSentinel.addEventListener('release', () => {
      wakeLockSentinel = null;
      wakeLockTotalRefCount = 0;
      wakeLockReasonCounts.clear();
    });
  } catch (e) {
    // Не фатально: низкий заряд/unsupported/и т.п.
    wakeLockSentinel = null;
  }
}

function tryForceReleaseWakeLock() {
  try {
    if (wakeLockSentinel) {
      wakeLockSentinel.release();
    }
  } catch (e) {
    // ignore
  } finally {
    wakeLockSentinel = null;
    wakeLockTotalRefCount = 0;
    wakeLockReasonCounts.clear();
  }
}

function getLikelyPageState() {
  // Нам важны hidden/frozen, а active/passive используется только для UI-логики.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return 'hidden';
  if (typeof document !== 'undefined' && typeof document.hasFocus === 'function' && document.hasFocus()) return 'active';
  return 'passive';
}

function lcDebugEnabled() {
  try {
    return typeof window !== 'undefined' && Boolean(window.__LC_DEBUG__);
  } catch (e) {
    return false;
  }
}

function lcLog(tag, extra = {}) {
  if (!lcDebugEnabled()) return;
  try {
    const s = typeof window !== 'undefined' ? window.__LC_STATE__ : null;
    // eslint-disable-next-line no-console
    console.log('[Lifecycle]', tag, {
      vis: typeof document !== 'undefined' ? document.visibilityState : null,
      focus: (typeof document !== 'undefined' && typeof document.hasFocus === 'function') ? document.hasFocus() : null,
      pageState: s && typeof s.pageState !== 'undefined' ? s.pageState : null,
      wakeLockActiveCount: s && typeof s.wakeLockActiveCount !== 'undefined' ? s.wakeLockActiveCount : null,
      pendingAbortModal: s ? s.pendingAbortModal : null,
      showAbortModal: s ? s.showAbortModal : null,
      abortToken: s ? s.abortToken : null,
      ...extra
    });
  } catch (e) {
    // ignore
  }
}

export default {
  state: {
    pageState: 'active', // active|hidden|frozen|passive

    // Важные операции (нужны для принятия решения — “сбрасывать” ли UI на hidden/frozen)
    // (оставлено под будущие расширения, но сейчас решение об abort берётся от WakeLock-активности)
    importantOperationTypes: [], // array<string>

    // Инкремент на каждый аварийный сброс (нужно для guard async-контуров)
    abortToken: 0,

    // Тайминг модалки: showAbortModal = показываем, pending = показать при возврате
    pendingAbortModal: false,
    showAbortModal: false,
    abortModalReason: null, // string

    // Версия для remount критичных компонентов
    photoUploadResetVersion: 0,

    // Список temp-photo id, который может сформироваться в WorkShifts (там фото не лежат в shiftReport.photos)
    // Используется для очистки на сервере при lifecycle abort.
    tempPhotoIdsForCleanup: [],

    // Реальная “важность” = активный wake lock (ref-count)
    wakeLockActiveCount: 0,

    // Snapshot состояния загрузчика PhotoUploadDevice (обновляется компонентом через emitStatus).
    // Используется в lifecycle_shouldSkipDestructiveAbort.
    photoDeviceStatus: {
      total: 0,
      hasUploading: false,
      errorCount: 0,
      allProcessed: false,
    },

    // Флаг: открыта ли модалка дозагрузки фото по смене (WorkShifts)
    workShiftPhotoModalActive: false,
  },

  getters: {
    isLifecycleAbortModalShown: (state) => state.showAbortModal,
    lifecycleAbortToken: (state) => state.abortToken,
    photoUploadResetVersion: (state) => state.photoUploadResetVersion,
    tempPhotoIdsForCleanup: (state) => state.tempPhotoIdsForCleanup,
    hasPendingAbortModal: (state) => Boolean(state.pendingAbortModal),
    importantOperationTypesCount: (state) => state.importantOperationTypes.length,
    isImportantOperationRunning: (state) => Number(state.wakeLockActiveCount) > 0,
    abortModalReason: (state) => state.abortModalReason,
    photoDeviceStatus: (state) => state.photoDeviceStatus,
    workShiftPhotoModalActive: (state) => state.workShiftPhotoModalActive,

    // Возвращает true, если destructive abort можно пропустить:
    // все temp-загрузки успешно завершены и данные формы/модалки в порядке.
    lifecycle_shouldSkipDestructiveAbort: (state, getters, rootState, rootGetters) => {
      const photoStatus = state.photoDeviceStatus;
      const allPhotosDone = (
        photoStatus.allProcessed &&
        photoStatus.total > 0 &&
        !photoStatus.hasUploading &&
        photoStatus.errorCount === 0
      );

      // // Ветка A: мастер сдачи отчёта (ShiftReport)
      // const sr = rootState.shiftReport;
      // if (sr) {
      //   const coreReady = rootGetters['shiftReport_isCoreFormReady'];
      //   const uploadByDevice = sr.uploadMethod === 'uploadByDevice';
      //   const photosOk = (
      //     !sr.photosNotAvailable &&
      //     Array.isArray(sr.photos) &&
      //     sr.photos.length > 0 &&
      //     sr.photos.every(p => p && p.id) &&
      //     sr.photos.length === photoStatus.total
      //   );
      //   if (coreReady && uploadByDevice && photosOk && allPhotosDone) {
      //     return true;
      //   }
      // }

      // // Ветка B: дозагрузка фото по смене (WorkShifts)
      // const ids = Array.isArray(state.tempPhotoIdsForCleanup) ? state.tempPhotoIdsForCleanup : [];
      // if (
      //   state.workShiftPhotoModalActive &&
      //   ids.length > 0 &&
      //   ids.length === photoStatus.total &&
      //   allPhotosDone
      // ) {
      //   return true;
      // }

      return allPhotosDone;
    },
  },

  actions: {
    lifecycle_init({ commit, dispatch, state, getters }) {
      if (typeof window === 'undefined') return;

      // Убираем повторную инициализацию (hot reload/повторные mount)
      if (window.__lifecycle_init_done__) return;
      window.__lifecycle_init_done__ = true;

      // Для отладочных логов (чтобы события могли читать текущее state)
      window.__LC_STATE__ = state;
      lcLog('init');
      commit('lifecycle_setPageState', getLikelyPageState());

      const showModalIfPending = () => {
        // Открываем модалку только когда страница видима
        const shouldShow = state.pendingAbortModal || getters.hasPendingAbortModal;
        if (!shouldShow) return;
        if (document && document.visibilityState !== 'hidden') {
          commit('lifecycle_setPendingAbortModal', false);
          commit('lifecycle_setShowAbortModal', true);
        }
      };

      const abortIfNeeded = (reason) => {
        if (!getters.isImportantOperationRunning) return;
        // Если уже в процессе abort для текущего token — не делаем повторно
        if (state.pendingAbortModal) return;
        lcLog('abortIfNeeded:start', { reason, important: true });
        // Делаем через action: тут возможны side-effects (deleteTempPhotos, сброс store)
        // События freeze/hidden могут вызываться в “неблагоприятный” момент,
        // поэтому мы НЕ делаем await и работаем best-effort.
        dispatch('lifecycle_abortIfNeeded', reason);
      };

      document.addEventListener(
        'freeze',
        () => {
          lcLog('event:frozen');
          commit('lifecycle_setPageState', 'frozen');
          abortIfNeeded('frozen');
        },
        { capture: true }
      );

      document.addEventListener(
        'resume',
        () => {
          lcLog('event:resume');
          commit('lifecycle_setPageState', getLikelyPageState());
          showModalIfPending();
        },
        { capture: true }
      );

      window.addEventListener(
        'visibilitychange',
        () => {
          const next = getLikelyPageState();
          lcLog('event:visibilitychange', { next, visibilityState: document && document.visibilityState });
          commit('lifecycle_setPageState', next);
          if (next === 'hidden') {
            abortIfNeeded('hidden');
          } else {
            showModalIfPending();
          }
        },
        { capture: true }
      );

      // pageshow — актуально для bfcache / возврата
      window.addEventListener(
        'pageshow',
        () => {
          lcLog('event:pageshow', { persisted: false });
          commit('lifecycle_setPageState', getLikelyPageState());
          showModalIfPending();
        },
        { capture: true }
      );

      // pagehide часто срабатывает при уходе/выгрузке, может соответствовать заморозке.
      window.addEventListener(
        'pagehide',
        (event) => {
          lcLog('event:pagehide', { persisted: event && event.persisted });
          // persisted=true означает уход в bfcache (часто frozen).
          if (event && event.persisted) {
            commit('lifecycle_setPageState', 'frozen');
            abortIfNeeded('frozen');
          }
        },
        { capture: true }
      );

      window.addEventListener(
        'focus',
        () => {
          lcLog('event:focus');
          // Страница может стать активной, а visibilityState ещё успевает обновиться.
          commit('lifecycle_setPageState', getLikelyPageState());
          showModalIfPending();
        },
        { capture: true }
      );
    },

    lifecycle_markImportant({ commit }, operationType) {
      if (!operationType) return;
      commit('lifecycle_addImportantOperationType', operationType);
    },

    lifecycle_unmarkImportant({ commit }, operationType) {
      if (!operationType) return;
      commit('lifecycle_removeImportantOperationType', operationType);
    },

    lifecycle_setPhotoDeviceStatus({ commit }, payload) {
      commit('lifecycle_setPhotoDeviceStatus', payload || {});
    },

    lifecycle_setWorkShiftPhotoModalActive({ commit }, value) {
      commit('lifecycle_setWorkShiftPhotoModalActive', Boolean(value));
    },

    lifecycle_setTempPhotoIdsForCleanup({ commit }, ids) {
      commit(
        'lifecycle_setTempPhotoIdsForCleanup',
        Array.isArray(ids) ? ids.filter(Boolean) : []
      );
    },

    async lifecycle_abortIfNeeded({ commit, dispatch, state, rootState, getters }, reason) {
      // Повторная защита (на случай если несколько событий прилетело подряд)
      if (state.pendingAbortModal) return;

      // Ранний выход: критичная отправка отчёта идёт — destructive abort запрещён.
      if (rootState.shiftReport && rootState.shiftReport.criticalSubmissionActive) {
        lcLog('abortIfNeeded:skipped:criticalSubmission', { reason });
        return;
      }

      // Ранний выход: все temp-загрузки успешны и данные формы/модалки в порядке.
      if (getters.lifecycle_shouldSkipDestructiveAbort) {
        lcLog('abortIfNeeded:skipped:tempUploadComplete', { reason });
        return;
      }

      lcLog('abortIfNeeded:begin', { reason });
      commit('lifecycle_markAbortPending', { reason });

      // ВАЖНО: ids нужно собрать ДО сброса shiftReport.photos,
      // иначе после commit('shiftReport_setPhotos', []) список окажется пустым.
      const idsFromShift = (rootState.shiftReport && Array.isArray(rootState.shiftReport.photos))
        ? rootState.shiftReport.photos.map(p => p && p.id).filter(Boolean)
        : [];
      const idsFromUi = Array.isArray(state.tempPhotoIdsForCleanup) ? state.tempPhotoIdsForCleanup : [];
      const ids = Array.from(new Set([...idsFromShift, ...idsFromUi]));

      lcLog('abortIfNeeded:ids', {
        idsCount: ids.length,
        idsFromShiftCount: idsFromShift.length,
        idsFromUiCount: idsFromUi.length
      });

      // 1) Сброс UI-данных на клиенте (важно сделать немедленно)
      commit('shiftReport_setOnProcess', false);

      // Сбрасываем именно фото-часть, чтобы пользователь вернулся на шаг 8 и выбрал заново.
      commit('shiftReport_setPhotos', []);
      commit('shiftReport_setPhotosNotAvailable', false);
      commit('shiftReport_setEmptyPhotoComment', null);
      commit('shiftReport_setUploadProgress', { done: 0, total: 0 });
      commit('shiftReport_setPhotosProcessingStatus', null);
      commit('shiftReport_setPhotosUploadStatus', null);
      commit('shiftReport_setPhotosUploadErrorMessage', null);
      commit('shiftReport_setReasonForDelay', null);

      // 2) Очистка temp-photo на сервере (best-effort)
      if (ids.length) {
        lcLog('abortIfNeeded:deleteTempPhotos', { ids });
        dispatch('shiftReport_deleteTempPhotos', ids).catch(() => {});
      }

      // 3) Чистим локальный список на будущее
      commit('lifecycle_setTempPhotoIdsForCleanup', []);

      // WakeLock в скрытом/замороженном состоянии смысла нет, а утечки — нежелательны.
      dispatch('wakeLock_forceRelease').catch(() => {});
    },

    // WakeLock actions — референс-счётчик в замыкании модуля
    wakeLock_acquire({ commit }, reason) {
      if (!reason) return;
      lcLog('wakeLock_acquire', { reason });
      const prev = wakeLockReasonCounts.get(reason) || 0;
      wakeLockReasonCounts.set(reason, prev + 1);
      wakeLockTotalRefCount += 1;
      // Если refCount > 0, запрашиваем lock (первый вызов сделает реальный request)
      if (!wakeLockSentinel && wakeLockTotalRefCount === 1) {
        tryAcquireWakeLock();
      }
      commit('lifecycle_setWakeLockActiveCount', wakeLockTotalRefCount);
    },

    wakeLock_release({ commit }, reason) {
      if (!reason) return;
      lcLog('wakeLock_release', { reason });
      const prev = wakeLockReasonCounts.get(reason) || 0;
      if (prev <= 0) return;
      const next = prev - 1;
      if (next <= 0) wakeLockReasonCounts.delete(reason);
      else wakeLockReasonCounts.set(reason, next);

      wakeLockTotalRefCount = Math.max(0, wakeLockTotalRefCount - 1);
      if (wakeLockTotalRefCount === 0) {
        tryForceReleaseWakeLock();
      }
      commit('lifecycle_setWakeLockActiveCount', wakeLockTotalRefCount);
    },

    wakeLock_forceRelease({ commit }) {
      lcLog('wakeLock_forceRelease');
      tryForceReleaseWakeLock();
      commit('lifecycle_setWakeLockActiveCount', 0);
    },

    // ACK модалки (пользователь явно нажал Ок)
    lifecycle_ackAbort({ commit }) {
      commit('lifecycle_setShowAbortModal', false);
      commit('lifecycle_setPendingAbortModal', false);
      commit('lifecycle_setAbortModalReason', null);
      commit('lifecycle_setTempPhotoIdsForCleanup', []);
    },
  },

  mutations: {
    lifecycle_setPageState(state, value) {
      state.pageState = value;
    },

    lifecycle_addImportantOperationType(state, type) {
      if (!type) return;
      if (!state.importantOperationTypes.includes(type)) {
        state.importantOperationTypes.push(type);
      }
    },

    lifecycle_removeImportantOperationType(state, type) {
      if (!type) return;
      state.importantOperationTypes = state.importantOperationTypes.filter(t => t !== type);
    },

    lifecycle_setPendingAbortModal(state, value) {
      state.pendingAbortModal = Boolean(value);
      lcLog('mutation:setPendingAbortModal', { value: state.pendingAbortModal });
    },

    lifecycle_setShowAbortModal(state, value) {
      state.showAbortModal = Boolean(value);
      lcLog('mutation:setShowAbortModal', { value: state.showAbortModal, reason: state.abortModalReason });
    },

    lifecycle_setAbortModalReason(state, value) {
      state.abortModalReason = value;
    },

    lifecycle_setTempPhotoIdsForCleanup(state, ids) {
      state.tempPhotoIdsForCleanup = Array.isArray(ids) ? ids.filter(Boolean) : [];
    },

    lifecycle_incrementPhotoUploadResetVersion(state) {
      state.photoUploadResetVersion += 1;
    },

    // Внутренняя точка: срабатывает в обработчиках hidden/frozen при наличии важной операции.
    // Здесь же делаем reset UI + запросы на удаление temp-файлов с сервера.
    lifecycle_markAbortPending(state, { reason }) {
      state.pageState = reason || 'hidden';
      state.abortToken += 1;
      state.pendingAbortModal = true;
      state.showAbortModal = false;
      state.abortModalReason = reason || 'hidden';
      state.importantOperationTypes = [];
      state.photoUploadResetVersion += 1;
    },

    lifecycle_setPhotoDeviceStatus(state, payload) {
      state.photoDeviceStatus = {
        total: Number(payload.total) || 0,
        hasUploading: Boolean(payload.hasUploading),
        errorCount: Number(payload.errorCount) || 0,
        allProcessed: Boolean(payload.allProcessed),
      };
    },

    lifecycle_setWorkShiftPhotoModalActive(state, value) {
      state.workShiftPhotoModalActive = Boolean(value);
    },

    // DEBUG: иногда удобно смотреть refCount в devtools
    lifecycle_setWakeLockActiveCount(state, value) {
      state.wakeLockActiveCount = Number(value) > 0 ? Number(value) : 0;
    },
  },
};

